import { ToastComponent } from '@tylertech/forge';
import { getMoneyObj } from '../reusable/MoneyJsHelpers.js';
import { calculatePercentileData, normalizeData, totalFteIsZeroGuard } from './AllocationsTable/allocationsTableHelpers.js';
import { COST_ALLOCATION_TOGGLE_BUTTON_LABELS } from './costAllocationHelpers.js';
import { messageManager } from '../System/messageManager/messageManager.js';

export const handleDataChangeWrapper = (
  params, 
  allocationMode, 
  selectedItemAllocations, 
  selectedItems, 
  positiveOnly,
  setSelectedItemAllocations,
  setSelectedItems,
  setPreviewPercent,
  setTotalProgramCost, 
  setCombinedTotalCostOfItems,
  setTotalAllocationCount
) => {
  const _selectedItemAllocations = structuredClone(selectedItemAllocations);
  const _selectedItems = structuredClone(selectedItems);

  const editedAllocation = _selectedItemAllocations.find(
    (allocation) => allocation.uniqueId === params.data.uniqueId
  );

  if (!editedAllocation) {
    return;
  }

  if (allocationMode === COST_ALLOCATION_TOGGLE_BUTTON_LABELS[0]) {
    handleDataChangeByPercent(params.newValue, editedAllocation, _selectedItemAllocations, _selectedItems);
  } else {
    handleDataChangeByCost(params.newValue, editedAllocation, _selectedItemAllocations, _selectedItems, positiveOnly);
  }

  setSelectedItemAllocations(_selectedItemAllocations);
  setSelectedItems(_selectedItems);
  calculateTotalsOnDataChange(
    _selectedItemAllocations, 
    _selectedItems,
    setPreviewPercent, 
    setTotalProgramCost, 
    setCombinedTotalCostOfItems,
    setTotalAllocationCount
  );
};


export const handleDataChangeByPercent = (newValue, editedAllocation, _selectedItemAllocations, _selectedItems) => {
  const sanitizedValue = Math.abs(newValue);
  editedAllocation.percent = sanitizedValue;
  
  recalculateByPercent(_selectedItemAllocations, _selectedItems);
};

export const handleDataChangeByCost = (newValue, editedAllocation, _selectedItemAllocations, _selectedItems, positiveOnly) => {
  const sanitizedValue = positiveOnly ? Math.abs(newValue) : -Math.abs(newValue);
  editedAllocation.dollars = sanitizedValue;
  
  recalculateByCost(_selectedItemAllocations, _selectedItems);
};

const recalculateByPercent = (_selectedItemAllocations, _selectedItems) => {
  // STEP 1: figure out which allocation percent value to change or ADD new allocation
  _selectedItemAllocations.forEach(allocation => {
    _selectedItems.forEach((item) => {
      let itemAllocation = item.allocations.find(a => a.programId === allocation.programId);
      if (!itemAllocation) {
        item.allocations.push({
          ...allocation,
          id: null,
          dollars: 0,
          percent: allocation.percent,
        });
      } else {
        itemAllocation.percent = allocation.percent;
        itemAllocation.dollars = 0;
      }
    });
  });

  // STEP 2: run money JS calculations to get precise dollar values
  _selectedItems.forEach(item => {
    const programPercents = (item.allocations || []).map(alloc => Number(alloc.percent));
    const allocatedPercents = programPercents.reduce((sum, percent) => {
      return sum.add(getMoneyObj(percent));
    }, getMoneyObj(0));

    let totalDollars = getMoneyObj(0);
    if (allocatedPercents.lessThanOrEqual(getMoneyObj(100))) {
      const money100 = getMoneyObj(100);
      const unallocatedPercents = money100.subtract(allocatedPercents);
      const proportionsArray = [...programPercents, unallocatedPercents.amount / 100].map(value => Math.abs(value));

      const dollarArray = getMoneyObj(item.total_cost).allocate(proportionsArray);

      item.allocations.forEach((allocation, i) => {
        allocation.dollars = Number((dollarArray[i].amount / 100).toFixed(2));
        totalDollars = totalDollars.add(dollarArray[i]);
      });
    } else {
      // item is overallocated, we cannot safely run Money js proportions on overallocated items
      // so we'll do simple (percents / 100) * total_cost calculations
      // UI will not let us save overallocated item
      item.allocations.forEach((allocation) => {
        allocation.dollars = Number(((allocation.percent / 100) * item.total_cost).toFixed(2));
        totalDollars = totalDollars.add(getMoneyObj(allocation.dollars));
      });
    }

    item.program_cost = totalDollars.amount / 100;
    item.allocation_percent = allocatedPercents.amount / 100;
  });

  // STEP 3: circle back and update _selectedItemAllocations dollar values
  _selectedItemAllocations.forEach(selectedAllocation => {
    let dollarsMoney = getMoneyObj(0);

    _selectedItems.forEach(item => {
      const itemAllocation = (item.allocations || []).find(allocation => allocation.programId === selectedAllocation.programId);

      if (itemAllocation) {
        dollarsMoney = dollarsMoney.add(getMoneyObj(itemAllocation.dollars));
      }
    });

    selectedAllocation.dollars = dollarsMoney.amount / 100;
  });
};

const recalculateByCost = (_selectedItemAllocations, _selectedItems) => {
  // STEP 1: figure out which allocation dollar value to change or ADD new allocation
  const dollarProportionsArray = _selectedItems.map((item) => Math.abs(item.total_cost));
  _selectedItemAllocations.forEach(allocation => {
    const { value, zeroed } = calculateChangeProportions(allocation.dollars, dollarProportionsArray);

    _selectedItems.forEach((item, i) => {
      let itemAllocation = item.allocations.find(a => a.programId === allocation.programId);

      if (!itemAllocation) {
        item.allocations.push({
          ...allocation,
          id: null,
          dollars: zeroed ? value : value[i].amount / 100,
          percent: 0,
        });
      } else {
        itemAllocation.dollars = zeroed ? value : value[i].amount / 100;
        itemAllocation.percent = 0;
      }
    });
  });
  
  // STEP 2: run money JS calculations to get precise percent values
  _selectedItems.forEach(item => {
    const programCosts = (item.allocations || []).map(alloc => Number(alloc.dollars));
    const allocatedDollars = programCosts.reduce((sum, cost) => {
      return sum.add(getMoneyObj(cost));
    }, getMoneyObj(0));

    const itemTotalCostMoney = getMoneyObj(item.total_cost);
    const unallocatedDollars = itemTotalCostMoney.subtract(allocatedDollars);
    const proportionsArray = [...programCosts, unallocatedDollars.amount / 100].map(value => Math.abs(value));

    const percentArray = getMoneyObj(100).allocate(proportionsArray);

    let totalPercents = getMoneyObj(0);
    item.allocations.forEach((allocation, i) => {
      allocation.percent = Number((percentArray[i].amount / 100).toFixed(2));
      totalPercents = totalPercents.add(percentArray[i]);
    });

    item.program_cost = allocatedDollars.amount / 100;
    item.allocation_percent = totalPercents.amount / 100;
  });

  // STEP 3: circle back and update _selectedItemAllocations percent values
  const combinedTotalCostMoney = _selectedItems.reduce((totalCost, item) => {
    return totalCost.add(getMoneyObj(item.total_cost));
  }, getMoneyObj(0));
  const programCosts = (_selectedItemAllocations || []).map(allocation => Number(allocation.dollars));
  const allocatedDollars = programCosts.reduce((sum, cost) => {
    return sum.add(getMoneyObj(cost));
  }, getMoneyObj(0));

  const unallocatedDollars = combinedTotalCostMoney.subtract(allocatedDollars);
  const proportionsArray = [...programCosts, unallocatedDollars.amount / 100].map(value => Math.abs(value));
  const percentArray = getMoneyObj(100).allocate(proportionsArray);

  _selectedItemAllocations.forEach((allocation, i) => {
    allocation.percent = Math.abs(percentArray[i].amount / 100);
  });
};

const calculateChangeProportions = (value, proportionsArray) => {
  let result = 0;
  
  let total = getMoneyObj(0);
  for (let i = 0; i < proportionsArray.length; i++) {
    total = total.add(getMoneyObj(proportionsArray[i]));
  }
  
  const zeroed = total.equals(getMoneyObj(0));
  if (zeroed) {
    result = value;
  } else {
    result = getMoneyObj(value).allocate(proportionsArray);
  }

  return { value: result, zeroed };
};


export const calculateTotalsOnDataChange = (
  allocations, 
  items, 
  setPreviewPercent, 
  setTotalProgramCost, 
  setCombinedTotalCostOfItems,
  setTotalAllocationCount
) => {
  if ((allocations || []).length === 0 || (items || []).length === 0) {
    return;
  }
  const _combinedTotalProgramCost = items.reduce(
    (sum, item) => sum.add(getMoneyObj(item.program_cost) || 0),
    getMoneyObj(0)
  );

  const _combinedTotalCostOfItems = items.reduce(
    (sum, item) => sum.add(getMoneyObj(item.total_cost) || 0),
    getMoneyObj(0)
  );

  const _previewPercent = Number(
    (
      (Math.abs(_combinedTotalProgramCost.amount) / Math.abs(_combinedTotalCostOfItems.amount)) *
      100
    ).toFixed(2)
  );

  setPreviewPercent(_previewPercent);
  setTotalProgramCost(_combinedTotalProgramCost);
  setCombinedTotalCostOfItems(_combinedTotalCostOfItems);
  setTotalAllocationCount(allocations.length);
};

export const handleClearAllocations = (
  selectedRows, 
  selectedItemAllocations, 
  selectedItems,
  setSelectedItemAllocations,
  setSelectedItems,
  setPreviewPercent, 
  setTotalProgramCost, 
  setCombinedTotalCostOfItems,
  setTotalAllocationCount,
  setCostingRecommendation
) => {
  const _selectedItemAllocations = structuredClone(selectedItemAllocations);
  const _selectedItems = structuredClone(selectedItems);
  const selectedRowIds = selectedRows.map((row) => row.uniqueId);

  _selectedItemAllocations.forEach((allocation) => {
    if (!selectedRowIds.includes(allocation.uniqueId)) {
      return;
    }

    _selectedItems.forEach(item => {
      const existingAllocation = (item.allocations || []).find(a => a.programId === allocation.programId);

      if (existingAllocation) {
        existingAllocation.percent = 0;
        existingAllocation.dollars = 0;
      }
    });

    allocation.percent = 0;
    allocation.dollars = 0;
  });

  _selectedItems.forEach(item => {
    item.program_cost = ((item.allocations || []).reduce((sum, allocation) => {
      return sum.add(getMoneyObj(Number(allocation.dollars)));
    }, getMoneyObj(0)).amount / 100);

    item.allocation_percent = ((item.allocations || []).reduce((sum, allocation) => {
      return sum.add(getMoneyObj(Number(allocation.percent)));
    }, getMoneyObj(0)).amount / 100);
  });

  setSelectedItemAllocations(_selectedItemAllocations);
  setSelectedItems(_selectedItems);
  calculateTotalsOnDataChange(
    _selectedItemAllocations, 
    _selectedItems, 
    setPreviewPercent, 
    setTotalProgramCost, 
    setCombinedTotalCostOfItems,
    setTotalAllocationCount
  );
  setCostingRecommendation(null);
};

export const handleAllocateEvenly = (
  allocationMode, 
  selectedRows, 
  selectedItemAllocations, 
  selectedItems,
  setSelectedItemAllocations, 
  setSelectedItems,
  setPreviewPercent, 
  setTotalProgramCost, 
  setCombinedTotalCostOfItems,
  setTotalAllocationCount,
  setCostingRecommendation
) => {
  if (allocationMode === COST_ALLOCATION_TOGGLE_BUTTON_LABELS[0]) {
    handleAllocateEvenlyByPercent(
      selectedRows, 
      selectedItemAllocations, 
      selectedItems,
      setSelectedItemAllocations, 
      setSelectedItems,
      setPreviewPercent, 
      setTotalProgramCost, 
      setCombinedTotalCostOfItems,
      setTotalAllocationCount,
      setCostingRecommendation
    );
  } else {
    handleAllocateEvenlyByCost(
      selectedRows, 
      selectedItemAllocations, 
      selectedItems,
      setSelectedItemAllocations, 
      setSelectedItems,
      setPreviewPercent, 
      setTotalProgramCost, 
      setCombinedTotalCostOfItems,
      setTotalAllocationCount,
      setCostingRecommendation
    );
  }
};

const handleAllocateEvenlyByPercent = (
  selectedRows, 
  selectedItemAllocations, 
  selectedItems, 
  setSelectedItemAllocations, 
  setSelectedItems,
  setPreviewPercent, 
  setTotalProgramCost, 
  setCombinedTotalCostOfItems,
  setTotalAllocationCount,
  setCostingRecommendation
) => {
  const _selectedItemAllocations = structuredClone(selectedItemAllocations);
  const _selectedItems = structuredClone(selectedItems);

  const selectedRowIds = selectedRows.map((row) => row.uniqueId);
  const selectedAllocations = _selectedItemAllocations.filter(itemAllocation => selectedRowIds.includes(itemAllocation.uniqueId));
  const unselectedAllocations = _selectedItemAllocations.filter(itemAllocation => !selectedRowIds.includes(itemAllocation.uniqueId));

  const unselectedTotalPercent = unselectedAllocations.reduce((total, allocation) => {
    return total.add(getMoneyObj(allocation.percent));
  }, getMoneyObj(0));

  const percent100 = getMoneyObj(100);
  const availablePercentage = percent100.subtract(unselectedTotalPercent);

  const proportionsArray = selectedAllocations.map(() => 1);
  const percentagesSpreading = availablePercentage.allocate(proportionsArray);

  selectedAllocations.forEach((allocation, i) => {
    allocation.percent = Number((percentagesSpreading[i].amount / 100).toFixed(2));
    allocation.dollars = 0;
  });

  recalculateByPercent(_selectedItemAllocations, _selectedItems);

  setSelectedItemAllocations(_selectedItemAllocations);
  setSelectedItems(_selectedItems);
  calculateTotalsOnDataChange(
    _selectedItemAllocations, 
    _selectedItems, 
    setPreviewPercent, 
    setTotalProgramCost, 
    setCombinedTotalCostOfItems,
    setTotalAllocationCount
  );
  setCostingRecommendation(null);
};

const handleAllocateEvenlyByCost = (
  selectedRows, 
  selectedItemAllocations, 
  selectedItems,
  setSelectedItemAllocations, 
  setSelectedItems,
  setPreviewPercent, 
  setTotalProgramCost, 
  setCombinedTotalCostOfItems,
  setTotalAllocationCount,
  setCostingRecommendation
) => {
  const _selectedItemAllocations = structuredClone(selectedItemAllocations);
  const _selectedItems = structuredClone(selectedItems);

  const selectedRowIds = selectedRows.map((row) => row.uniqueId);
  const selectedAllocations = _selectedItemAllocations.filter(itemAllocation => selectedRowIds.includes(itemAllocation.uniqueId));
  const unselectedAllocations = _selectedItemAllocations.filter(itemAllocation => !selectedRowIds.includes(itemAllocation.uniqueId));

  const unselectedTotalDollars = unselectedAllocations.reduce((total, allocation) => {
    return total.add(getMoneyObj(allocation.dollars));
  }, getMoneyObj(0));

  const combinedTotalCost = _selectedItems.reduce((total, allocation) => {
    return total.add(getMoneyObj(allocation.total_cost));
  }, getMoneyObj(0));

  const availableMoney = combinedTotalCost.subtract(unselectedTotalDollars);
  const proportionsArray = selectedAllocations.map(() => 1);
  const dollarSpreading = availableMoney.allocate(proportionsArray);

  selectedAllocations.forEach((allocation, i) => {
    allocation.dollars = Number((dollarSpreading[i].amount / 100).toFixed(2));
    allocation.percent = 0;
  });

  recalculateByCost(_selectedItemAllocations, _selectedItems);

  setSelectedItemAllocations(_selectedItemAllocations);
  setSelectedItems(_selectedItems);
  calculateTotalsOnDataChange(
    _selectedItemAllocations, 
    _selectedItems, 
    setPreviewPercent, 
    setTotalProgramCost, 
    setCombinedTotalCostOfItems,
    setTotalAllocationCount
  );
  setCostingRecommendation(null);
};

export const handleRecommendAllocations = (
  recommendationData, 
  selectedItemAllocations, 
  selectedItems,
  availablePercent,
  setSelectedItemAllocations, 
  setSelectedItems,
  setPreviewPercent, 
  setTotalProgramCost, 
  setCombinedTotalCostOfItems,
  setTotalAllocationCount,
  setCostingRecommendation
) => {
  const _selectedItemAllocations = structuredClone(selectedItemAllocations);
  const _selectedItems = structuredClone(selectedItems);

  const percentileData = calculatePercentileData(
    recommendationData.data,
    _selectedItems.length
  );

  const normalizedData = normalizeData(
    percentileData,
    availablePercent,
    _selectedItems
  );

  _selectedItemAllocations.forEach((allocation) => {
    const recommendedProgram = normalizedData.find(
      (program) => program.programId === allocation.programId
    );

    allocation.percent = recommendedProgram ? recommendedProgram.nMedian : allocation.percent;
    allocation.dollars = 0;
  });

  recalculateByPercent(_selectedItemAllocations, _selectedItems);

  setSelectedItemAllocations(_selectedItemAllocations);
  setSelectedItems(_selectedItems);
  calculateTotalsOnDataChange(
    _selectedItemAllocations, 
    _selectedItems, 
    setPreviewPercent, 
    setTotalProgramCost, 
    setCombinedTotalCostOfItems,
    setTotalAllocationCount
  );
  setCostingRecommendation(normalizedData);
};

export const handleAllocateByFte = (
  selectedRows, 
  selectedItemAllocations, 
  selectedItems,
  setSelectedItemAllocations, 
  setSelectedItems,
  setPreviewPercent, 
  setTotalProgramCost, 
  setCombinedTotalCostOfItems,
  setTotalAllocationCount,
  setCostingRecommendation,
  nameConventions
) => {
  const _selectedItemAllocations = structuredClone(selectedItemAllocations);
  const _selectedItems = structuredClone(selectedItems);

  const selectedRowIds = selectedRows.map((row) => row.uniqueId);
  const selectedAllocations = _selectedItemAllocations.filter(itemAllocation => selectedRowIds.includes(itemAllocation.uniqueId));
  const unselectedAllocations = _selectedItemAllocations.filter(itemAllocation => !selectedRowIds.includes(itemAllocation.uniqueId));

  const unselectedTotalDollars = unselectedAllocations.reduce((total, allocation) => {
    return total.add(getMoneyObj(allocation.dollars));
  }, getMoneyObj(0));

  const combinedTotalCost = _selectedItems.reduce((total, allocation) => {
    return total.add(getMoneyObj(allocation.total_cost));
  }, getMoneyObj(0));

  const proportionsArray = selectedAllocations.map(allocation => Number(allocation.byFte));

  const isFteZero = totalFteIsZeroGuard(proportionsArray);
  if (isFteZero) {
    ToastComponent.present({
      message: messageManager(nameConventions).COST_ALLOCATION.FTE_TOTAL_ZERO_ERROR,
    });
    return;
  }

  const availableMoney = combinedTotalCost.subtract(unselectedTotalDollars);
  const dollarSpreading = availableMoney.allocate(proportionsArray);

  selectedAllocations.forEach((allocation, i) => {
    allocation.dollars = Number((dollarSpreading[i].amount / 100).toFixed(2));
    allocation.percent = 0;
  });

  recalculateByCost(_selectedItemAllocations, _selectedItems);

  setSelectedItemAllocations(_selectedItemAllocations);
  setSelectedItems(_selectedItems);
  calculateTotalsOnDataChange(
    _selectedItemAllocations, 
    _selectedItems, 
    setPreviewPercent, 
    setTotalProgramCost, 
    setCombinedTotalCostOfItems,
    setTotalAllocationCount
  );
  setCostingRecommendation(null);
};

export const recalculateMultipleItemsAllocationsOnEdit = (allocationMode, selectedItemAllocations, selectedItems) => {
  if (allocationMode === COST_ALLOCATION_TOGGLE_BUTTON_LABELS[0]) {
    recalculateByPercent(selectedItemAllocations, selectedItems);
  } else {
    recalculateByCost(selectedItemAllocations, selectedItems);
  }
};
